import { createPopper, Instance, Options } from '@popperjs/core';
import {
  MutableRefObject,
  RefCallback,
  RefObject,
  useCallback,
  useMemo,
  useRef,
} from 'react';

export function usePopper(
  options?: Partial<Options>,
): [
  RefCallback<Element | null>,
  RefCallback<HTMLElement | null>,
  RefObject<Element | null>,
  RefObject<HTMLElement | null>,
  MutableRefObject<Instance | undefined>,
] {
  const reference = useRef<Element>(null);
  const popper = useRef<HTMLElement>(null);

  const cleanupCallback = useRef<Instance>(undefined);

  const instantiatePopper = useCallback(() => {
    if (!reference.current) return;
    if (!popper.current) return;

    if (cleanupCallback.current) cleanupCallback.current.destroy();

    cleanupCallback.current = createPopper(
      reference.current,
      popper.current,
      options,
    );
  }, [reference, popper, cleanupCallback, options]);

  return useMemo(
    () => [
      (referenceDomNode) => {
        reference.current = referenceDomNode;
        instantiatePopper();
      },
      (popperDomNode) => {
        popper.current = popperDomNode;
        instantiatePopper();
      },
      reference,
      popper,
      cleanupCallback,
    ],
    [reference, popper, cleanupCallback, instantiatePopper],
  );
}
